import React from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import styled from "styled-components";

import { HeaderText, SectionContainer, SectionSubtitle } from "../../../components/Modals";
import { Flex } from "../../../components/ui/Flex";
import ToggleInput from "../../../components/ui/ToggleInput";
import { USER_PIN_LENGTH_DROPDOWN_OPTIONS } from "../../users/constant";
import { Select, SelectItem } from "../../../components/ui/Select";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../components/ui/Form";
import { Palette } from "../../../components/utils";
import { IoChevronForward } from "react-icons/io5";
import { isICTLockProvider } from "../../../utils/lockInfo";

const UserAccessFormInputs = ({ form }) => {
  const { t } = useTranslation();
  const lockProvider = useWatch({
    control: form.control,
    name: "lock_provider"
  });
  const hideResidentCredentialIssuance = isICTLockProvider(lockProvider);

  return (
    <SectionContainer>
      <HeaderText alt>{t('User Access & Roles')}</HeaderText>
      <SectionSubtitle>{t('Set permissions and PIN behaviors for admins and residents.')}</SectionSubtitle>
      <Flex direction="column" gap="16px">
        <ToggleInput
          form={form}
          name="show_user_pin_admin"
          label={t('Show Master PIN (Admin)')}
          description={t('Display master PIN in Admin and Manager app')}
        />

        <ToggleInput
          form={form}
          name="show_user_pin_resident"
          label={t('Show User PIN (Resident)')}
          description={t('Allow residents to view their user and guest PINs')}
        />

        <ToggleInput
          form={form}
          name="show_user_pin_to_admin"
          label={t('Show User PIN to Admin')}
          description={t("Grant admins visibility of residents' user and guest PINs")}
        />

        <ToggleInput
          form={form}
          name="enable_mfa"
          label={t('Enable Multifactor Authentication')}
          description={t('Require multi-factor authentication for user sign-in.')}
        />

        <ToggleInput
          form={form}
          name="org_allow_edits"
          label={t('Allow Edit')}
          description={t('Enable edit/delete users, add/edit buildings, units, amenities')}
        />

        <ToggleInput
          form={form}
          name="enable_add_guests"
          label={t('Enable Add Guests')}
          description={t("Residents can grant limited app access for guests to their unit.")}
        />

        <ToggleInput
          form={form}
          name="enable_add_co_residents"
          label={t('Enable Add Coresident')}
          description={t("Residents can add co-residents to their unit with full app permissions.")}
        />

        {!hideResidentCredentialIssuance && (
          <ToggleInput
            form={form}
            name="allow_resident_credential_issuance"
            label={t('Allow Resident Credential Issuance')}
            description={t('Allow residents to issue mobile credentials.')}
          />
        )}

        <FormField
          control={form.control}
          name="user_pin_len"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('User PIN Length')}</FormLabel>
              <FormControl>
                <Select placeholder={t('Please select')} {...field}>
                  {USER_PIN_LENGTH_DROPDOWN_OPTIONS.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PINLengthInfoContainer>
          <IoChevronForward size={20} />
          <p>{t('PIN must be 4-10 digits')}</p>
        </PINLengthInfoContainer>
      </Flex>
    </SectionContainer>
  )
};

export default UserAccessFormInputs;

const PINLengthInfoContainer = styled.div`
  display: flex;
  gap: 0.125rem;
  align-items: center;

  p {
    margin-bottom: 0;
    font-size: 12px;
    color: ${Palette.HAZY_BLUE};
  }
`;
